import { __extends } from "tslib";
import { SpecularCompositeMethod } from './SpecularCompositeMethod';
/**
 * SpecularFresnelMethod provides a specular shading method that causes stronger
 * highlights on grazing view angles.
 */
var SpecularFresnelMethod = /** @class */ (function (_super) {
    __extends(SpecularFresnelMethod, _super);
    /**
     * Creates a new SpecularFresnelMethod object.
     * @param basedOnSurface Defines whether the fresnel effect should be based
     * on the view angle on the surface (if true), or on the angle between the
     * light and the view.
     * @param baseMethod The specular method to which the fresnel equation.
     * Defaults to SpecularBasicMethod.
     */
    function SpecularFresnelMethod(basedOnSurface, fresnelPower, normalReflectance, baseMethod) {
        if (basedOnSurface === void 0) { basedOnSurface = true; }
        if (fresnelPower === void 0) { fresnelPower = 5; }
        if (normalReflectance === void 0) { normalReflectance = 0.028; }
        if (baseMethod === void 0) { baseMethod = null; }
        var _this = _super.call(this, baseMethod) || this;
        _this._basedOnSurface = basedOnSurface;
        _this._fresnelPower = fresnelPower;
        _this._normalReflectance = normalReflectance;
        return _this;
    }
    Object.defineProperty(SpecularFresnelMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return SpecularFresnelMethod.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SpecularFresnelMethod.prototype, "basedOnSurface", {
        /**
         * Defines whether the fresnel effect should be based on the view angle on
         * the surface (if true), or on the angle between the light and the view.
         */
        get: function () {
            return this._basedOnSurface;
        },
        set: function (value) {
            if (this._basedOnSurface == value)
                return;
            this._basedOnSurface = value;
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SpecularFresnelMethod.prototype, "fresnelPower", {
        /**
         * The power used in the Fresnel equation. Higher values make the fresnel
         * effect more pronounced. Defaults to 5.
         */
        get: function () {
            return this._fresnelPower;
        },
        set: function (value) {
            if (this._fresnelPower == value)
                return;
            this._fresnelPower = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SpecularFresnelMethod.prototype, "normalReflectance", {
        /**
         * The minimum amount of reflectance, ie the reflectance when the view
         * direction is normal to the surface or light direction.
         */
        get: function () {
            return this._normalReflectance;
        },
        set: function (value) {
            if (this._normalReflectance == value)
                return;
            this._normalReflectance = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    SpecularFresnelMethod.assetType = '[asset SpecularFresnelMethod]';
    return SpecularFresnelMethod;
}(SpecularCompositeMethod));
export { SpecularFresnelMethod };
import { ShaderBase } from '@awayjs/renderer';
import { _Shader_LightingCompositeMethod } from './CompositeMethodBase';
/**
 * _Shader_SpecularFresnelMethod provides a specular shading method that causes
 * stronger highlights on grazing view angles.
 */
var _Shader_SpecularFresnelMethod = /** @class */ (function (_super) {
    __extends(_Shader_SpecularFresnelMethod, _super);
    /**
     * Creates a new _Shader_SpecularFresnelMethod object.
     */
    function _Shader_SpecularFresnelMethod(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        _this._baseChunk._modulateFunction = function (targetReg, registerCache, sharedRegisters) { return _this.modulateSpecular(targetReg, registerCache, sharedRegisters); };
        return _this;
    }
    /**
     * @inheritDoc
     */
    _Shader_SpecularFresnelMethod.prototype._cleanCompilationData = function () {
        _super.prototype._cleanCompilationData.call(this);
        this._fresnelDataRegister = null;
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularFresnelMethod.prototype._activate = function () {
        _super.prototype._activate.call(this);
        if (this._invalid) {
            var data = this._shader.fragmentConstantData;
            var index = this._fresnelDataIndex;
            data[index] = this._method.normalReflectance;
            data[index + 1] = this._method.fresnelPower;
            data[index + 2] = 1;
            data[index + 3] = 0;
        }
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularFresnelMethod.prototype._getFragmentPreLightingCode = function (registerCache, sharedRegisters) {
        this._fresnelDataRegister = registerCache.getFreeFragmentConstant();
        this._fresnelDataIndex = this._fresnelDataRegister.index * 4;
        return _super.prototype._getFragmentPreLightingCode.call(this, registerCache, sharedRegisters);
    };
    /**
     * Applies the fresnel effect to the specular strength.
     *
     * @param vo The MethodVO object containing the method data for the
     * currently compiled material pass.
     * @param target The register containing the specular strength in the "w"
     * component, and the half-vector/reflection vector in "xyz".
     * @param regCache The register cache used for the shader compilation.
     * @param sharedRegisters The shared registers created by the compiler.
     * @return The AGAL fragment code for the method.
     */
    _Shader_SpecularFresnelMethod.prototype.modulateSpecular = function (targetReg, registerCache, sharedRegisters) {
        var code;
        code = 'dp3 ' + targetReg + '.y, ' + sharedRegisters.viewDirFragment + '.xyz, ' + (this._method.basedOnSurface ? sharedRegisters.normalFragment : targetReg) + '.xyz\n' + // dot(V, H)
            'sub ' + targetReg + '.y, ' + this._fresnelDataRegister + '.z, ' + targetReg + '.y\n' + // base = 1-dot(V, H)
            'pow ' + targetReg + '.x, ' + targetReg + '.y, ' + this._fresnelDataRegister + '.y\n' + // exp = pow(base, 5)
            'sub ' + targetReg + '.y, ' + this._fresnelDataRegister + '.z, ' + targetReg + '.y\n' + // 1 - exp
            'mul ' + targetReg + '.y, ' + this._fresnelDataRegister + '.x, ' + targetReg + '.y\n' + // f0*(1 - exp)
            'add ' + targetReg + '.y, ' + targetReg + '.x, ' + targetReg + '.y\n' + // exp + f0*(1 - exp)
            'mul ' + targetReg + '.w, ' + targetReg + '.w, ' + targetReg + '.y\n';
        return code;
    };
    return _Shader_SpecularFresnelMethod;
}(_Shader_LightingCompositeMethod));
export { _Shader_SpecularFresnelMethod };
ShaderBase.registerAbstraction(_Shader_SpecularFresnelMethod, SpecularFresnelMethod);
